import { useMutation, useQueryClient } from "@tanstack/react-query";
import { titleQueryKeys } from "@/lib/react-query/keyFactory";
import { internalNoteCreate, internalNoteDelete } from "@/proofingService-client/sdk.gen";
import type {
  InternalNoteCreateData,
  InternalNoteCreateResponse,
  InternalNoteDeleteResponse,
} from "@/proofingService-client/types.gen";

export interface UseCreateInternalNoteOptions {
  onSuccess?: (data: InternalNoteCreateResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for creating a new internal note under a proofing title.
 * Handles API calls, loading states, error handling, and cache invalidation.
 *
 * @param options - Optional callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 */
export const useCreateInternalNote = (options?: UseCreateInternalNoteOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      titleId,
      data,
    }: {
      titleId: string;
      data: InternalNoteCreateData["body"];
    }) => {
      const response = await internalNoteCreate({
        path: { titleId },
        body: data,
      });
      if (!response.data) {
        throw new Error("Failed to create internal note");
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch internal notes list for this title
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.internalNotes(variables.titleId) });
      // Invalidate all title queries to update any title-level data
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      console.error("Failed to create internal note:", error);
      options?.onError?.(error);
    },
  });
};

export interface UseDeleteInternalNoteOptions {
  onSuccess?: (data: InternalNoteDeleteResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * React Query mutation hook for deleting an internal note from a proofing title.
 * Handles API calls, loading states, error handling, and cache invalidation.
 *
 * @param options - Optional callbacks for success and error handling
 * @returns Mutation object with mutate function and state
 */
export const useDeleteInternalNote = (options?: UseDeleteInternalNoteOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ titleId, noteId }: { titleId: string; noteId: string }) => {
      const response = await internalNoteDelete({
        path: { titleId, noteId },
      });
      if (!response.data) {
        throw new Error("Failed to delete internal note");
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch internal notes list for this title
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.internalNotes(variables.titleId) });
      // Invalidate all title queries to update any title-level data
      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all() });
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      console.error("Failed to delete internal note:", error);
      options?.onError?.(error);
    },
  });
};
